import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, Loader2, RefreshCw, Sparkles, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static tips as fallback
const STATIC_TIPS = {
  memoir: {
    1: { title: "The Moment Everything Changed", proTip: "Start with a vivid scene. Ground us in time and place." },
    default: { title: "Your Chapter", proTip: "Every chapter should reveal something new about yourself or your journey." }
  },
  fiction: {
    1: { title: "The World Before", proTip: "Show us who your hero is before the adventure begins." },
    default: { title: "Continue the Journey", proTip: "Every chapter should change something. What's different by the end?" }
  },
  freestyle: {
    default: { title: "Your Chapter", proTip: "Follow your creative instinct. Trust your voice." }
  }
};

function ChapterCoverage({ chapterId, chapterNumber, genre, content }) {
  const [isExpanded, setIsExpanded] = useState(false); // Start CLOSED
  const [dynamicGuide, setDynamicGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastAnalyzedLength, setLastAnalyzedLength] = useState(0);
  const [lastAnalyzedWordCount, setLastAnalyzedWordCount] = useState(0);
  const [hasReachedThreshold, setHasReachedThreshold] = useState(false);
  const [hasNewGuidance, setHasNewGuidance] = useState(false);
  const [reentryContext, setReentryContext] = useState(null);

  // Fetch dynamic guide when content changes significantly OR when chapter changes
  useEffect(() => {
    const contentLength = content?.length || 0;
    const wordCount = content ? content.trim().split(/\s+/).filter(w => w.length > 0).length : 0;
    const wordDifference = Math.abs(wordCount - lastAnalyzedWordCount);
    
    // Special case: User just crossed the 50-word threshold
    // Switch from general to context-specific guidance immediately
    if (chapterId && wordCount >= 50 && !hasReachedThreshold) {
      setHasReachedThreshold(true);
      fetchDynamicGuide();
      return;
    }
    
    // Refresh only after a substantial stretch of new writing. This was 20 words
    // with a 2s debounce, which fired an LLM call roughly every other sentence —
    // uncapped, untracked, and far more often than the guidance actually changes.
    // The Refresh button below covers the case where someone wants it sooner.
    if (chapterId && wordCount >= 50 && wordDifference >= 250) {
      const timer = setTimeout(() => {
        fetchDynamicGuide();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [content, chapterId, hasReachedThreshold, lastAnalyzedWordCount]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Separate effect: Auto-fetch when chapter changes (immediate, no debounce)
  useEffect(() => {
    if (chapterId) {
      // Clear previous guidance when switching chapters
      setDynamicGuide(null);
      setReentryContext(null);
      setHasReachedThreshold(false);
      setHasNewGuidance(false);
      setIsExpanded(false);
      
      // Always fetch guidance immediately, even for empty chapters
      // Backend provides chapter-specific tips based on narrative position
      fetchDynamicGuide();
    }
  }, [chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDynamicGuide = async () => {
    if (!chapterId) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/chapters/${chapterId}/writing-guide`,
        { current_text: content || "" },
        
      );
      setDynamicGuide(response.data);

      // Re-entry: auto-open panel with book context when chapter is empty but book has history
      if (response.data?.reentry_context) {
        setReentryContext(response.data.reentry_context);
        setIsExpanded(true);
      }

      // Update tracking
      const wordCount = content ? content.trim().split(/\s+/).filter(w => w.length > 0).length : 0;
      setLastAnalyzedLength(content?.length || 0);
      setLastAnalyzedWordCount(wordCount);

      // If panel is collapsed and we have guidance, notify user with glowing lightbulb
      if (!isExpanded && response.data?.guidance && !response.data?.reentry_context) {
        setHasNewGuidance(true);
      }
    } catch (error) {
      console.error('Failed to fetch writing guide:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle expand/collapse - clear notification when user opens
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setHasNewGuidance(false); // Clear notification when user opens
    }
  };

  // Get static fallback
  const staticTips = STATIC_TIPS[genre]?.[chapterNumber] || 
                     STATIC_TIPS[genre]?.default || 
                     STATIC_TIPS.freestyle.default;

  const guidance = dynamicGuide?.guidance;
  const hasContent = dynamicGuide?.has_content;
  
  // Extract new data structure
  const focusPrompt = guidance?.focus_prompt;
  const storyQuestions = guidance?.story_questions || [];
  const quickIdeas = guidance?.quick_ideas || [];

  return (
    <div 
      className="bg-[#FFFCF7]/58 dark:bg-[#211E1A]/52 backdrop-blur-sm border border-[#E8DED0]/60 dark:border-[#302820]/55 rounded-xl overflow-hidden"
      data-testid="chapter-coverage"
    >
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-[#F8F5F0]/55 dark:hover:bg-[#2A2520]/45 transition-colors"
      >
        <div className="flex items-center space-x-2">
          {/* Animated lightbulb when new guidance arrives */}
          <div className="relative">
            <Lightbulb
              className={`w-4 h-4 transition-all duration-300 ${
                hasNewGuidance
                  ? 'text-[#5C4D6B] dark:text-[#B0A0C0] drop-shadow-[0_0_8px_rgba(92,77,107,0.4)]'
                  : 'text-[#6B5A7B] dark:text-[#9A8AA8]'
              }`}
            />
            {hasNewGuidance && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#5C4D6B] dark:bg-[#B0A0C0] rounded-full" />
            )}
          </div>
          
          <span className="font-medium text-sm text-[#2A2420] dark:text-[#F0EBE4]">
            Writing Guide
          </span>
          
          {hasNewGuidance && (
            <span className="text-xs bg-[#EDE8F4] dark:bg-[#2A2035]/50 text-[#5C4D6B] dark:text-[#B0A0C0] px-2 py-0.5 rounded-full font-medium border border-[#C4B0D0]/50 dark:border-[#5C4D6B]/40">
              Updated
            </span>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#5C4D6B] dark:text-[#B0A0C0]" />}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-[#2A2420]/60 dark:text-[#F0EBE4]/60" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#2A2420]/60 dark:text-[#F0EBE4]/60" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 space-y-3">

          {/* Re-entry card — shown when opening an empty chapter with prior book history */}
          {reentryContext && (
            <div className="bg-[#F5F0EA] dark:bg-[#1E1C1A] border border-[#D4C4A8] dark:border-[#3A3028] rounded-lg p-3">
              <p className="text-xs font-semibold text-[#7C6650] dark:text-[#A89070] mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Where your book left off
              </p>
              <p className="text-sm text-[#3D3530] dark:text-[#C8BFB4] leading-snug mb-2">
                {reentryContext.last_chapter_note}
              </p>
              {reentryContext.open_thread && (
                <p className="text-xs text-[#7C766E] dark:text-[#8C847A] border-t border-[#E0D8CC] dark:border-[#2A2520]/50 pt-2 mt-2">
                  Still open: {reentryContext.open_thread}
                </p>
              )}
              {reentryContext.people_so_far?.length > 0 && (
                <p className="text-xs text-[#7C766E] dark:text-[#8C847A] mt-1">
                  In your story: {reentryContext.people_so_far.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Focus Prompt */}
          {focusPrompt && (
            <div className="bg-[#F0EBE4] dark:bg-[#1C1A18] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Sparkles className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#2A2420] dark:text-[#E8DFD4]">
                  {focusPrompt}
                </p>
              </div>
            </div>
          )}

          {/* Story Questions */}
          {storyQuestions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-[#7C766E] dark:text-[#8C847A] flex items-center gap-1">
                <HelpCircle className="w-3 h-3" />
                Questions to consider
              </p>
              {storyQuestions.map((question, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-lg px-3 py-2">
                  <span className="text-[#B0A898] dark:text-[#4A4440] flex-shrink-0 mt-0.5 text-xs font-medium tabular-nums">{idx + 1}.</span>
                  <p className="text-sm text-[#3D3530] dark:text-[#C8BFB4]">{question}</p>
                </div>
              ))}
            </div>
          )}

          {/* Quick Ideas */}
          {quickIdeas.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-[#7C766E] dark:text-[#8C847A] flex items-center gap-1">
                <Lightbulb className="w-3 h-3" />
                Try this
              </p>
              {quickIdeas.map((idea, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="text-[#5C4D6B] dark:text-[#B0A0C0] flex-shrink-0 mt-0.5">→</span>
                  <p className="text-sm text-[#5C5248] dark:text-[#A09890]">{idea}</p>
                </div>
              ))}
            </div>
          )}

          {/* Fallback static tip */}
          {!focusPrompt && !storyQuestions.length && !quickIdeas.length && (
            <div className="bg-[#F0EBE4] dark:bg-[#1C1A18] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-lg p-3">
              <p className="text-sm text-[#3D3530] dark:text-[#C8BFB4]">{staticTips.proTip}</p>
            </div>
          )}

          {/* Refresh Button (show when there's guidance to refresh) */}
          {(focusPrompt || storyQuestions.length > 0 || quickIdeas.length > 0) && (
            <Button
              onClick={fetchDynamicGuide}
              disabled={loading}
              variant="outline"
              size="sm"
              className="w-full text-xs border-[#E0D8CC] dark:border-[#2A2520]/50 text-[#7C766E] dark:text-[#8C847A] hover:bg-[#F0EBE4]/50 dark:hover:bg-[#2A2520]/30"
              data-testid="refresh-guide"
            >
              {loading ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              Refresh Guidance
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default ChapterCoverage;
